import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BulkUploadModal = ({ onClose }) => {
  const [selectedDataType, setSelectedDataType] = useState('products');
  const [file, setFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const dataTypes = [
    { value: 'products', label: 'Products', icon: 'Package' },
    { value: 'technicians', label: 'Technicians', icon: 'UserCog' },
    { value: 'customers', label: 'Customers', icon: 'Users' }
  ];

  const handleFileChange = (e) => {
    const selectedFile = e?.target?.files?.[0];
    if (selectedFile) {
      const fileExtension = selectedFile?.name?.split('.')?.pop()?.toLowerCase();
      if (fileExtension === 'csv' || fileExtension === 'json') {
        setFile(selectedFile);
        setUploadStatus(null);
      } else {
        setUploadStatus({ type: 'error', message: 'Please upload a CSV or JSON file' });
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setUploadStatus({ type: 'error', message: 'Please select a file to upload' });
      return;
    }

    setIsUploading(true);
    setUploadStatus({ type: 'info', message: 'Processing file...' });

    try {
      // Simulate file processing
      await new Promise(resolve => setTimeout(resolve, 2000));

      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          let data;
          const fileContent = e?.target?.result;

          if (file?.name?.endsWith('.json')) {
            data = JSON.parse(fileContent);
          } else if (file?.name?.endsWith('.csv')) {
            data = parseCSV(fileContent);
          }

          // Validate data structure
          if (!Array.isArray(data) || data?.length === 0) {
            throw new Error('Invalid file format. Expected an array of records.');
          }

          // Here you would integrate with Supabase to insert data
          // For now, we'll simulate success
          setUploadStatus({
            type: 'success',
            message: `Successfully processed ${data?.length} ${selectedDataType} records. Ready to import.`
          });

          // Reset after success
          setTimeout(() => {
            setFile(null);
            setUploadStatus(null);
            onClose();
          }, 2000);
        } catch (error) {
          setUploadStatus({
            type: 'error',
            message: `Error processing file: ${error?.message}`
          });
        } finally {
          setIsUploading(false);
        }
      };

      reader?.readAsText(file);
    } catch (error) {
      setUploadStatus({
        type: 'error',
        message: 'Failed to upload file. Please try again.'
      });
      setIsUploading(false);
    }
  };

  const parseCSV = (csvText) => {
    const lines = csvText?.split('\n')?.filter(line => line?.trim());
    if (lines?.length < 2) return [];

    const headers = lines?.[0]?.split(',')?.map(h => h?.trim());
    let data = [];

    for (let i = 1; i < lines?.length; i++) {
      const values = lines?.[i]?.split(',')?.map(v => v?.trim());
      const row = {};
      headers?.forEach((header, index) => {
        row[header] = values?.[index];
      });
      data?.push(row);
    }

    return data;
  };

  const downloadTemplate = () => {
    let templateData = '';
    let filename = '';

    switch (selectedDataType) {
      case 'products':
        templateData = 'name,sku,category,description,price,cost,quantity,reorderLevel,supplier,unit,isTrackable\nSample Product,SKU-001,Category,Description,99.99,50.00,100,20,Supplier Name,EA,true';
        filename = 'products_template.csv';
        break;
      case 'technicians':
        templateData = 'name,role,email,phone,status,location,skills,equipment\nJohn Doe,Technician,john@example.com,(555) 123-4567,Available,Downtown,HVAC|Electrical,Multimeter|Tool Kit';
        filename = 'technicians_template.csv';
        break;
      case 'customers':
        templateData = 'name,email,phone,company,location,address,status\nJane Smith,jane@company.com,(555) 987-6543,Company Inc,New York NY,123 Main St,Active';
        filename = 'customers_template.csv';
        break;
      default:
        return;
    }

    const blob = new Blob([templateData], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
      <div className="bg-card rounded-lg shadow-elevation-3 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-heading font-semibold text-foreground">
              Bulk Data Upload
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Import multiple records from CSV or JSON files
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-smooth"
          >
            <Icon name="X" size={20} color="var(--color-foreground)" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Data Type Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Select Data Type
            </label>
            <div className="grid grid-cols-3 gap-3">
              {dataTypes?.map((type) => (
                <button
                  key={type?.value}
                  onClick={() => setSelectedDataType(type?.value)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-smooth ${
                    selectedDataType === type?.value
                      ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                  }`}
                >
                  <Icon
                    name={type?.icon}
                    size={24}
                    color={selectedDataType === type?.value ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                  />
                  <span className={`text-sm font-medium ${
                    selectedDataType === type?.value ? 'text-primary' : 'text-foreground'
                  }`}>
                    {type?.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Upload File (CSV or JSON)
            </label>
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary transition-smooth">
              <input
                type="file"
                accept=".csv,.json"
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <Icon name="Upload" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                <p className="text-foreground font-medium mb-2">
                  {file ? file?.name : 'Click to upload or drag and drop'}
                </p>
                <p className="text-sm text-muted-foreground">
                  CSV or JSON files only
                </p>
              </label>
            </div>
          </div>

          {/* Status Message */}
          {uploadStatus && (
            <div className={`p-4 rounded-lg flex items-start gap-3 ${
              uploadStatus?.type === 'success' ? 'bg-green-500/10 border border-green-500/20' :
              uploadStatus?.type === 'error'? 'bg-red-500/10 border border-red-500/20' : 'bg-blue-500/10 border border-blue-500/20'
            }`}>
              <Icon
                name={uploadStatus?.type === 'success' ? 'CheckCircle' : uploadStatus?.type === 'error' ? 'AlertCircle' : 'Info'}
                size={20}
                color={uploadStatus?.type === 'success' ? '#22c55e' : uploadStatus?.type === 'error' ? '#ef4444' : '#3b82f6'}
              />
              <p className={`text-sm ${
                uploadStatus?.type === 'success' ? 'text-green-700' :
                uploadStatus?.type === 'error'? 'text-red-700' : 'text-blue-700'
              }`}>
                {uploadStatus?.message}
              </p>
            </div>
          )}

          {/* Template Download */}
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="FileText" size={20} color="var(--color-primary)" />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground mb-1">
                  Need a template?
                </p>
                <p className="text-xs text-muted-foreground mb-3">
                  Download a sample CSV template with the correct format for {selectedDataType}
                </p>
                <button
                  onClick={downloadTemplate}
                  className="text-sm text-primary hover:underline font-medium"
                >
                  Download Template
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!file || isUploading}
            className="flex items-center gap-2"
          >
            {isUploading ? (
              <>
                <Icon name="Loader" size={16} color="currentColor" className="animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Icon name="Upload" size={16} color="currentColor" />
                Upload Data
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;